import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Input from '../components/ui/Input';
import Button from '../components/ui/Button';
import { LogIn, AlertCircle } from 'lucide-react';

const loginStyles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'var(--color-bg-primary)',
    padding: 'var(--space-lg)',
    background: 'radial-gradient(ellipse at center, var(--color-bg-secondary) 0%, var(--color-bg-primary) 70%)',
  },
  card: {
    width: '100%',
    maxWidth: '420px',
    backgroundColor: 'var(--color-bg-card)',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid rgba(212, 175, 55, 0.15)',
    boxShadow: 'var(--shadow-lg)',
    overflow: 'hidden',
  },
  header: {
    padding: 'var(--space-2xl) var(--space-xl) var(--space-lg)',
    textAlign: 'center',
    borderBottom: '1px solid rgba(212, 175, 55, 0.1)',
  },
  brand: {
    fontFamily: 'var(--font-display)',
    fontSize: '2rem',
    fontWeight: 700,
    color: 'var(--color-primary)',
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    margin: 0,
    textShadow: '0 0 30px rgba(212, 175, 55, 0.3)',
  },
  subtitle: {
    fontFamily: 'var(--font-display)',
    fontSize: '0.8rem',
    color: 'var(--color-text-muted)',
    letterSpacing: '0.15em',
    textTransform: 'uppercase',
    marginTop: 'var(--space-xs)',
    marginBottom: 0,
  },
  body: {
    padding: 'var(--space-xl)',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-md)',
  },
  error: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-sm)',
    padding: 'var(--space-sm) var(--space-md)',
    backgroundColor: 'rgba(231, 76, 60, 0.1)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid rgba(231, 76, 60, 0.2)',
    color: 'var(--color-danger)',
    fontSize: '0.85rem',
  },
  footer: {
    padding: 'var(--space-md) var(--space-xl) var(--space-xl)',
    textAlign: 'center',
  },
  footerText: {
    fontSize: '0.8rem',
    color: 'var(--color-text-muted)',
    margin: 0,
  },
};

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const data = await login(email, password);
      const user = data.user || data;
      if (user.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/control');
      }
    } catch (err) {
      const message =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        'Credenziali non valide';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={loginStyles.page}>
      <div style={loginStyles.card}>
        <div style={loginStyles.header}>
          <h1 style={loginStyles.brand}>Affari Tuoi</h1>
          <p style={loginStyles.subtitle}>Pannello di Controllo</p>
        </div>

        <div style={loginStyles.body}>
          <form onSubmit={handleSubmit} style={loginStyles.form}>
            {error && (
              <div style={loginStyles.error}>
                <AlertCircle size={16} />
                <span>{error}</span>
              </div>
            )}

            <Input
              label="Email"
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@affarituoi.it"
              required
            />

            <Input
              label="Password"
              type="password"
              name="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="La tua password"
              required
            />

            <Button
              variant="primary"
              size="lg"
              type="submit"
              loading={loading}
              style={{ width: '100%', marginTop: 'var(--space-sm)' }}
            >
              <LogIn size={18} />
              Accedi
            </Button>
          </form>
        </div>

        <div style={loginStyles.footer}>
          <p style={loginStyles.footerText}>
            Non hai un account?{' '}
            <Link to="/register" style={{ color: 'var(--color-primary)', textDecoration: 'none', fontWeight: 600 }}>
              Registrati
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
